import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import { useGetCenterQuery, useUpdateCenterMutation } from "../../../services/ConfigurationService";
import { CenterType } from "../../../services/ConfigurationService/types/centerType";
import { CenterHeader } from "../components/sections";
import { PhysiciansTab, PayersTab, ServicesTab, ProceduresTab, RoomsTab, AppConfigTab, MedicareConfigTab, OutlierConfigTab, RefundConfigTab, WriteoffConfigTab, ItemMasterTab, ReasonMappingTab } from "../components/tabs";
import { PageLoading as Loading } from "../../common/components";
import PageCard from '../../common/components/styled-ui-elements/PageCard';

interface CenterDetailTabProps {
  id: string;
  form: CenterType;
}

// Indexed by tab position so switching tabs is a lookup, not a branch chain.
const CENTER_DETAIL_TABS: Array<(props: CenterDetailTabProps) => React.ReactNode> = [
  () => <PhysiciansTab />,
  () => <PayersTab />,
  () => <ServicesTab />,
  () => <ProceduresTab />,
  () => <RoomsTab />,
  () => <AppConfigTab />,
  () => <MedicareConfigTab />,
  () => <OutlierConfigTab />,
  ({ id, form }) => <RefundConfigTab centerId={id} initialConfig={form.refundApprovalConfig} />,
  ({ id, form }) => <WriteoffConfigTab centerId={id} initialConfig={form.writeoffApprovalConfig} />,
  ({ id, form }) => <ItemMasterTab facilityId={form.code} />,
  () => <ReasonMappingTab />,
];

function CenterDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get("company") || "";

  // Fetch center details
  const { data: center, isLoading, error } = useGetCenterQuery({ id: id || "", company });
  const [updateCenter, { isLoading: isUpdating }] = useUpdateCenterMutation();
  const [form, setForm] = React.useState<CenterType | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [tabIndex, setTabIndex] = React.useState(0);

  React.useEffect(() => {
    if (center) setForm(center);
  }, [center]);

  const handleSave = async () => {
    if (!form) return;
    await updateCenter(form);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
  };

  if (isLoading || !form)
    return (
      <Box sx={{ p: 3 }}>
        <Loading />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <PageCard>
          <Box sx={{ mt: 2 }}>
            <Typography color="error" variant="h6" align="center">
              Error loading center details
            </Typography>
          </Box>
        </PageCard>
      </Box>
    );

  return (
    <Box sx={{ p: 3 }}>
      <PageCard>
        <CenterHeader
            form={form}
            isUpdating={isUpdating}
            success={success}
            onSave={handleSave}
            onCenterUpdate={(updated) => setForm({ ...form, ...updated })}
          />

          {/* Content Section with Tabs */}
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                mb: 3,
                '& .MuiTab-root': {
                  minWidth: 120,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.95rem'
                }
              }}
              TabIndicatorProps={{ 
                style: { 
                  height: 3, 
                  borderRadius: 2, 
                  background: '#3b82f6' 
                } 
              }}
            >
              <Tab label="Physicians" />
              <Tab label="Payers" />
              <Tab label="Services" />
              <Tab label="Procedures" />
              <Tab label="Rooms" />
              <Tab label="App Config" />
              <Tab label="Medicare Config" />
              <Tab label="Outlier Config" />
              <Tab label="Refund Config" />
              <Tab label="WriteOff Config" />
              <Tab label="Item Master" />
              <Tab label="Reason Mapping" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ minHeight: 400 }}>
              {CENTER_DETAIL_TABS[tabIndex]?.({ id: id || '', form })}
            </Box>
          </Box>
      </PageCard>
    </Box>
  );
}

export { CenterDetail };

